import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '~/components/Layout';
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery';
import i18n from '~/components/i18n';
import TableComponent from '~/components/TableComponent';
import { Dropdown, Icon, Input, Popup } from 'semantic-ui-react';
import CustomIcon from '~/components/CustomIcon';
import { useDebounce } from 'use-lodash-debounce';
import styled from 'styled-components';
import ExcelExport from '~/components/ExcelExport';
import { RouteTypes } from '~/routes/constants';
import RegistrationViewModal from './RegistrationViewModal';
import FarmersList from '../FeedPurchases/FarmersList';
import { El } from '~/components/Layout/style';
import { useNavigate } from 'react-router';

const FiltersWrapper = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
`;

const getTableColumns = () => [
  {
    name: i18n.t('EVS Nr.'),
    key: 'evsNumber'
  },
  {
    name: i18n.t('Registration date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName'
  },
  {
    name: i18n.t('Street'),
    key: 'street',
    type: 'street'
  },
  {
    name: i18n.t('Zip'),
    key: 'zip'
  },
  {
    name: i18n.t('City'),
    key: 'city'
  },
  {
    name: i18n.t('Actions')
  }
];

const Registration: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const actions = [
    {
      popupMessage: t('Registration details'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'view'
    },
    {
      popupMessage: i18n.t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit',
      renderActions: getActions
    }
  ]

  function getActions(row, actionType) {
    switch (actionType) {
      case 'edit': {
        const name = `${row.companyName}${row.evsNumber ? ` (${row.evsNumber})` : ''}`
        return (
          <Popup
            content={t('Edit')}
            trigger={
              <CustomIcon
                style={{ cursor: 'pointer' }}
                name='pencil'
                size='large'
                onClick={() => navigate(`${RouteTypes.CLIMA_PROGRAM_REGISTRATION_EDIT.replace(':id', row.id)}#farmerName=${encodeURIComponent(name)}`)}
              />
            }
          />
        )
      }
      default:
        return
    }
  }

  const navigate = useNavigate()
  const [registrationId, setRegistrationId] = useState(null)
  const [selectedFarmerId, setSelectedFarmerId] = useState<string | null>(null)
  const [selectedFarmerRegistration, setSelectedFarmerRegistration] = useState<boolean | null>(null)
  const [farmerName, setFarmerName] = useState('')
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    year: currentYear,
    search: ''
  });
  const [search, setSearch] = useState('');
  const debouncedSearchValue = useDebounce(search, 300);
  const tableColumnHeaders = getTableColumns()

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i;
    return { key: year, value: year, text: year.toString() };
  });

  const {
    data: registrations,
    isLoading: isLoadingRegistrations,
    isFetching: isFetchinReg,
    refetch: fetchRegistrations
  } = ClimaProgramQuery.getRegistrations({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  useEffect(() => {
    fetchRegistrations()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function handleActionClick(row, type) {
    switch (type) {
      case 'view':
        return setRegistrationId(row?.id)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchinReg}
      animated
      actions={actions}
      tableColumnHeaders={tableColumnHeaders}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      rows={rows}
      total={filtered}
    />
  }

  function renderFilters() {
    return (
      <FiltersWrapper>
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('EVS No. / Company Name')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
        <Dropdown
          selection
          selectOnBlur={false}
          value={dataFiltered.year}
          options={yearOptions}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, year: data.value as number, page: 0 })}
        />
      </FiltersWrapper>
    );
  }

  function renderRegistrationViewModal() {
    return <RegistrationViewModal
      registrationId={registrationId}
      onClose={() => setRegistrationId(null)}
    />
  }

  function renderActions() {
    return <>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <FarmersList
          value={selectedFarmerId}
          onChange={(value, climateProgramRegistration, name) => {
            setSelectedFarmerId(value)
            setSelectedFarmerRegistration(climateProgramRegistration)
            setFarmerName(name || '')
          }}
          clearable
        />
        {selectedFarmerId && selectedFarmerRegistration === null && <El.PageHeaderActionButton
          onClick={() => navigate(`${RouteTypes.CLIMA_PROGRAM_REGISTRATION_CREATE.replace(':idFarmer', selectedFarmerId)}#farmerName=${encodeURIComponent(farmerName)}`)}
        >
          <Icon name='plus' />
          {t('Register')}
        </El.PageHeaderActionButton>}
        {selectedFarmerId && selectedFarmerRegistration !== null && <Popup
          content={t('Farmer is registered')}
          trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
        />}
      </div>
      <ExcelExport
        url={'climate-program/registrations/export'}
        fileName=''
        icon={'file excel'}
        downloadWithFileName
        data={{
          year: dataFiltered?.year
        }}
      />
    </>
  }

  const { filtered, rows } = registrations ? registrations : { filtered: 0, rows: [] };

  return (
    <Layout
      title={t('Registration')}
      segmented
      isBusy={isLoadingRegistrations}
      filters={renderFilters()}
      actions={renderActions()}
    >
      {renderRegistrationViewModal()}
      {renderTable()}
    </Layout>
  );
};

export default Registration;
